import { useQuery } from '@apollo/client';
import { Label, Stack, Text } from '@fluentui/react';
import { FormHookDropdown } from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { FormHookTimePicker } from 'common/components/FormHooksFields/FormHookTimePicker';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { NotificationCycleValues } from '../types';
import { NotificationCycleTimeZones } from './__generated__/NotificationCycleTimeZones';
import { NotifyTypes } from './__generated__/NotifyTypes';
import { useStyles } from './index.styles';
import TIMEZONES from './NotificationCycleTimeZones.graphql';
import NOTIFY_TYPES from './NotifyTypes.graphql';

type BasicFormProps = {
  isUpdatable: boolean;
};

export const BasicForm: React.FC<BasicFormProps> = ({ isUpdatable }) => {
  const styles = useStyles();

  const { data: notifyTypesData } = useQuery<NotifyTypes>(NOTIFY_TYPES, {
    variables: {
      orderBy: ['SORT_ORDER_ASC'],
    },
  });
  const { data: timeZonesData } = useQuery<NotificationCycleTimeZones>(
    TIMEZONES,
    {
      variables: {
        orderBy: ['DESCRIPTION_ASC', 'ACTIVE_TIME_ZONE_CODE_ASC']
      },
    }
  );
  const typeOptions =
    notifyTypesData?.notifyTypes?.nodes.map((item) => ({
      key: item.id,
      text: item.description || '',
    })) || [];

  const timeZonesOptions =
    timeZonesData?.timeZones?.nodes.map((item) => ({
      key: item.id,
      text: item.description || '',
    })) || [];

  const { getValues } = useFormContext<NotificationCycleValues>();
  const lastRunValue = getValues('lastRun');

  return (
    <Stack
      tokens={{
        padding: 20,
        childrenGap: 10,
      }}
      className={styles.container}
    >

      <Stack
        className={styles.fieldContainer}
        tokens={{
          childrenGap: 15,
        }}
      >
        <FormHookDropdown
          label="Type"
          placeholder="Select"
          options={typeOptions}
          name="notifyTypeId"
          disabled={!isUpdatable}
          required
        />
        <FormHookDropdown
          label="Time Zone"
          placeholder="Select"
          options={timeZonesOptions}
          name="timeZoneId"
          disabled={!isUpdatable}
          required
        />
        <Label>Select days to send notifications</Label>
        <Stack
          horizontal
          wrap
          tokens={{ childrenGap: 30 }}
          styles={{ root: { width: '100%', paddingBottom: 10 } }}
        >
          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Monday"
              name="isSentMonday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Tuesday"
              name="isSentTuesday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Wednesday"
              name="isSentWednesday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Thursday"
              name="isSentThursday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Friday"
              name="isSentFriday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Saturday"
              name="isSentSaturday"
              disabled={!isUpdatable}
            />
          </Stack.Item>

          <Stack.Item className={styles.checkboxItemStyle}>
            <FormHookCheckBox
              label="Sunday"
              name="isSentSunday"
              disabled={!isUpdatable}
            />
          </Stack.Item>
        </Stack>

        <Label>Cycle hours -</Label>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 50 }}>
          <Label>Start send time :</Label>
          <FormHookTimePicker
            name={`startSendTime`}
            disabled={!isUpdatable}
          />
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 55 }}>
          <Label>End send time :</Label>

          <FormHookTimePicker name={`endSendTime`} disabled={!isUpdatable} />
        </Stack>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 95 }}>
          <Label>Last run :</Label>
          <Text>{lastRunValue?.length ? lastRunValue : ''}</Text>
        </Stack>
      </Stack>
    </Stack>
  );
};
